const channels: { [key: string]: BroadcastPolyfill[] } = {}

/**
 * Polyfill implementation for broadcast channel
 */
class BroadcastPolyfill {
  /**
   * name of the channel
   */
  public readonly name: string

  /**
   * wheter or not this channel is closed
   */
  public closed = false

  /**
   * internal message channel
   */
  private readonly mc: MessageChannel = new MessageChannel()

  get onmessage(): ((this: MessagePort, ev: MessageEvent) => any) | null {
    return this.mc.port1.onmessage
  }

  set onmessage(value: (event: MessageEvent<any>) => void) {
    this.mc.port1.onmessage = value
  }

  get onmessageerror(): ((this: MessagePort, ev: MessageEvent) => any) | null {
    return this.mc.port1.onmessageerror
  }

  set onmessageerror(value: (event: MessageEvent<any>) => void) {
    this.mc.port1.onmessageerror = value
  }

  constructor(name: string) {
    this.name = name
    channels[name] = channels[name] || []
    channels[name].push(this)

    // Initialize message channel
    this.mc.port1.start()
    this.mc.port2.start()

    // Add event listeners
    this.onStorage = this.onStorage.bind(this)
    window.addEventListener('storage', this.onStorage)
  }

  private onStorage(event: StorageEvent) {
    if (event.storageArea !== window.localStorage) return
    if (event.key?.substring(0, this.name.length) !== this.name) return
    if (event.newValue === null) return
    const data = JSON.parse(event.newValue)
    this.mc.port2.postMessage(data)
  }

  public postMessage(message: any) {
    if (this.closed) throw new Error('InvalidStateError')
    const value = JSON.stringify(message)
    const key = `${this.name}:${String(Date.now())}${String(Math.random())}`

    // Broadcast to remote contexts via storage events
    window.localStorage.setItem(key, value)
    setTimeout(() => {
      window.localStorage.removeItem(key)
    }, 500)

    // Broadcast to current context via ports
    channels[this.name].forEach((bc) => {
      if (bc === this) return
      bc.mc.port2.postMessage(JSON.parse(value))
    })
  }

  public close() {
    if (this.closed) return
    this.closed = true
    this.mc.port1.close()
    this.mc.port2.close()

    window.removeEventListener('storage', this.onStorage)

    const index = channels[this.name].indexOf(this)
    channels[this.name].splice(index, 1)
  }

  public addEventListener(event: keyof MessagePortEventMap, listener: (event: MessageEvent<any>) => void) {
    return this.mc.port1.addEventListener(event, listener)
  }

  public removeEventListener(event: keyof MessagePortEventMap, listener: (event: MessageEvent<any>) => void) {
    return this.mc.port1.removeEventListener(event, listener)
  }

  public dispatchEvent(event: Event): boolean {
    return this.mc.port1.dispatchEvent(event)
  }
}

export const BroadcastChannel =
  typeof window === 'undefined'
    ? undefined
    : typeof window.BroadcastChannel === 'function'
    ? window.BroadcastChannel
    : BroadcastPolyfill
